import { api } from 'api';

// #region Types
export enum BaconCommandKeys {
  FILTER = 'f',
  AXIS = 'a',
  VALUE = 'v',
  HIDE_VALUE = 'hv',
  PIVOT = 'p',
  ORDER = 'o',
}

export const isBaconCommandKey = (
  command: string
): command is BaconCommandKeys =>
  command === BaconCommandKeys.FILTER ||
  command === BaconCommandKeys.AXIS ||
  command === BaconCommandKeys.VALUE ||
  command === BaconCommandKeys.HIDE_VALUE ||
  command === BaconCommandKeys.PIVOT ||
  command === BaconCommandKeys.ORDER;

export enum BaconOperators {
  EQUALS = 'eq',
  NOT_EQUALS = 'ne',
  GREATER_THAN = 'gt',
  GREATER_THAN_OR_EQUAL = 'ge',
  LESS_THAN_OR_EQUAL = 'le',
  SUBSET = 'subsetof',
  SUPERSET = 'supersetof',
  DISJOINT = 'disjointfrom',
  EQUALS_SET = 'equals',
  NOT_SUBSET = 'notsubsetof',
  NOT_SUPERSET = 'notsupersetof',
  INTERSECTS = 'intersects',
  NOT_EQUALS_SET = 'notequals',
  IN = 'in',
  NOT_IN = 'ni',
  HAS_ALL = 'hasall',
  HAS_ANY = 'hasany',
  HAS_ONLY = 'hasonly',
  HAS_NONE = 'hasnone',
}

export const isBaconOperator = (operator: string): operator is BaconOperators =>
  operator === BaconOperators.EQUALS ||
  operator === BaconOperators.NOT_EQUALS ||
  operator === BaconOperators.GREATER_THAN ||
  operator === BaconOperators.GREATER_THAN_OR_EQUAL ||
  operator === BaconOperators.LESS_THAN_OR_EQUAL ||
  operator === BaconOperators.SUBSET ||
  operator === BaconOperators.SUPERSET ||
  operator === BaconOperators.DISJOINT ||
  operator === BaconOperators.EQUALS_SET ||
  operator === BaconOperators.NOT_SUBSET ||
  operator === BaconOperators.NOT_SUPERSET ||
  operator === BaconOperators.INTERSECTS ||
  operator === BaconOperators.NOT_EQUALS_SET ||
  operator === BaconOperators.IN ||
  operator === BaconOperators.NOT_IN ||
  operator === BaconOperators.HAS_ALL ||
  operator === BaconOperators.HAS_ANY ||
  operator === BaconOperators.HAS_ONLY ||
  operator === BaconOperators.HAS_NONE;

export const BACON_SET_OPERATORS = new Set<BaconOperators>([
  BaconOperators.SUBSET,
  BaconOperators.SUPERSET,
  BaconOperators.DISJOINT,
  BaconOperators.EQUALS_SET,
  BaconOperators.NOT_SUBSET,
  BaconOperators.NOT_SUPERSET,
  BaconOperators.INTERSECTS,
  BaconOperators.NOT_EQUALS_SET,
]);
export const BACON_HAS_OPERATORS = new Set<BaconOperators>([
  BaconOperators.IN,
  BaconOperators.NOT_IN,
  BaconOperators.HAS_ALL,
  BaconOperators.HAS_ANY,
  BaconOperators.HAS_ONLY,
  BaconOperators.HAS_NONE,
]);
export const BACON_MULTIPLE_VALUE_OPERATORS = new Set<BaconOperators>([
  ...BACON_SET_OPERATORS,
  ...BACON_HAS_OPERATORS,
]);
export const BACON_MULTIPLE_VALUE_SEPERATOR = ':';
export const DEFAULT_BACON_OPERATOR = BaconOperators.EQUALS;

export interface BaseBaconQueryPart {
  command: BaconCommandKeys;
  column: string;
}

export interface BaconSingleFilterQueryPart extends BaseBaconQueryPart {
  command: BaconCommandKeys.FILTER;
  operator: BaconOperators;
  value: string;
  column: string;
}

export interface BaconMultiFilterQueryPart extends BaseBaconQueryPart {
  command: BaconCommandKeys.FILTER;
  operator: BaconOperators;
  value: string[];
  column: string;
}

export type BaconFilterQueryPart =
  | BaconSingleFilterQueryPart
  | BaconMultiFilterQueryPart;

export interface BaconAxisQueryPart extends BaseBaconQueryPart {
  command: BaconCommandKeys.AXIS;
  value: string;
}

export interface BaconValueQueryPart extends BaseBaconQueryPart {
  command: BaconCommandKeys.VALUE;
  value: string;
}

export interface BaconHideValueQueryPart extends BaseBaconQueryPart {
  command: BaconCommandKeys.HIDE_VALUE;
  value: string;
}

export interface BaconPivotQueryPart extends BaseBaconQueryPart {
  command: BaconCommandKeys.PIVOT;
  value: string;
}

export interface BaconOrderQueryPart extends BaseBaconQueryPart {
  command: BaconCommandKeys.ORDER;
  value: string;
}

export type BaconQueryPart =
  | BaconFilterQueryPart
  | BaconAxisQueryPart
  | BaconValueQueryPart
  | BaconHideValueQueryPart
  | BaconPivotQueryPart
  | BaconOrderQueryPart;

export const isBaconFilterQueryPart = (
  queryPart: BaconQueryPart
): queryPart is BaconFilterQueryPart =>
  queryPart.command === BaconCommandKeys.FILTER;
export const isBaconSingleFilterQueryPart = (
  queryPart: BaconQueryPart
): queryPart is BaconSingleFilterQueryPart =>
  isBaconFilterQueryPart(queryPart) &&
  !BACON_MULTIPLE_VALUE_OPERATORS.has(queryPart.operator);
export const isBaconMultiFilterQueryPart = (
  queryPart: BaconQueryPart
): queryPart is BaconMultiFilterQueryPart =>
  isBaconFilterQueryPart(queryPart) &&
  BACON_MULTIPLE_VALUE_OPERATORS.has(queryPart.operator);
export const isBaconAxisQueryPart = (
  queryPart: BaconQueryPart
): queryPart is BaconValueQueryPart =>
  queryPart.command === BaconCommandKeys.AXIS;
export const isBaconValueQueryPart = (
  queryPart: BaconQueryPart
): queryPart is BaconValueQueryPart =>
  queryPart.command === BaconCommandKeys.VALUE;
export const isBaconHideValueQueryPart = (
  queryPart: BaconQueryPart
): queryPart is BaconHideValueQueryPart =>
  queryPart.command === BaconCommandKeys.HIDE_VALUE;
export const isBaconPivotQueryPart = (
  queryPart: BaconQueryPart
): queryPart is BaconPivotQueryPart =>
  queryPart.command === BaconCommandKeys.PIVOT;
export const isBaconOrderQueryPart = (
  queryPart: BaconQueryPart
): queryPart is BaconOrderQueryPart =>
  queryPart.command === BaconCommandKeys.ORDER;

export enum BaconColumnType {
  LABEL = 'label',
  VALUE = 'value',
  TOTAL = 'total',
  ACTION = 'action',
}

export enum BaconWidgetType {
  DATE_RANGE = 'dates_range',
  BUTTONS = 'buttons',
}

export interface BaconColumnLinks {
  drop_axis?: string;
  hide?: string;
  pivot?: string;
  reset_order?: string;
  order?: string;
  order_asc?: string;
}

export interface BaconPivotLinks {
  pivot?: string;
  drop_axis?: string;
}

export interface BaconPivotValueLinks {
  hide?: string;
  filter?: string;
}

export interface BaconPivotValue {
  label: string;
  links: BaconPivotValueLinks;
}

export interface BaconColumn {
  label: string;
  total?: string;
  links: BaconColumnLinks;
  type: BaconColumnType;
}

export interface BaconLinks {
  [key: string]: string;
}

export interface BaconPivots {
  label: string;
  links: BaconPivotLinks;
  values: BaconPivotValue[];
}

export interface BaconPage {
  label: string;
  url?: string;
  current: boolean;
}

export type BaconCell =
  | string
  | number
  | [value: string | number, options: { filter: string }];

export interface BaconAxis {
  label: string;
  url: string;
}

export interface BaconDimension {
  dimension: string;
  axes: BaconAxis[];
}

export interface BaconFilter {
  label: string;
  drop_url: string;
}

export interface BaconValue {
  label: string;
  show_url?: string;
}

export interface BaconHideValue {
  label: string;
  hide_url?: string;
}

export interface BaconBaseWidget {
  label: string;
  type: BaconWidgetType;
}

export interface BaconButtonWidget extends BaconBaseWidget {
  type: BaconWidgetType.BUTTONS;
  buttons: {
    image_url?: string;
    label: string;
    name: string;
    url: string;
  }[];
}

export interface BaconDateRangeWidget extends BaconBaseWidget {
  type: BaconWidgetType.DATE_RANGE;
  urls: {
    no_value: string;
    from_only: string;
    to_only: string;
    both_values: string;
  };
  values: {
    from?: string;
    to?: string;
  };
}

export type BaconWidget = BaconButtonWidget | BaconDateRangeWidget;

export const isBaconButtonWidget = (
  widget: BaconWidget
): widget is BaconButtonWidget => widget.type === BaconWidgetType.BUTTONS;

export const isBaconDateRangeWidget = (
  widget: BaconWidget
): widget is BaconDateRangeWidget => widget.type === BaconWidgetType.DATE_RANGE;

export interface BaconTable {
  columns: BaconColumn[];
  links: BaconLinks;
  pivots: BaconPivots[];
  pages?: BaconPage[];
  rows: BaconCell[][];
}

export const DEFAULT_BACON_TABLE: BaconTable = {
  columns: [],
  links: {},
  pivots: [],
  rows: [],
};

export interface BaconNav {
  axes: BaconDimension[];
  filters: BaconFilter[];
  values: BaconValue[];
  hideValues: BaconHideValue[];
  widgets: BaconWidget[];
}

export const DEFAULT_BACON_NAV: BaconNav = {
  axes: [],
  filters: [],
  values: [],
  hideValues: [],
  widgets: [],
};
// #endregion

// #region Utilities
class BaconError extends Error {}
// #endregion

// #region Main Class
/**
 * A class to handle bacon query generation and fetching.
 */
export class BaconEngine {
  baseUrl: string;
  endpoint: string;
  query = '';
  localQuery = '';
  pagination = '';
  localPagination = '';
  nav: BaconNav = DEFAULT_BACON_NAV;
  table: BaconTable = DEFAULT_BACON_TABLE;

  constructor(endpoint: string, baseUrl: string = window.urls.reports) {
    this.endpoint = endpoint.endsWith('/') ? endpoint.slice(0, -1) : endpoint;
    this.baseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  }

  /**
   * Set the local query and recalculate necessary values.
   */
  setQuery(query: string) {
    this.localQuery = query;
  }
  setPagination(pagination: string) {
    this.localPagination = pagination;
  }

  cleanQueryUrl(url?: string) {
    let _url = url?.split('q=').slice(-1)[0].split('&')[0];

    if (_url?.endsWith('/')) {
      _url = _url.slice(0, -1);
    }

    return _url;
  }

  /**
   * Gets a list of query parts with there consituent parts split out into
   * an object. Also provides raw query part string.
   *
   * query defaults to current BaconEngine query
   */
  getQueryParts(query?: string) {
    return (query || this.query)
      .split('/')
      .filter((part) => !!part)
      .map((partString: string): BaconQueryPart & { raw: string } => {
        const [command, column, _operator, __value] = partString.split(':');

        if (!isBaconCommandKey(command))
          throw new BaconError(
            `Unrecognised bacon command '${command}' in '${partString}'.`
          );

        if (command !== BaconCommandKeys.FILTER) {
          return {
            raw: partString,
            command: command,
            column,
          } as BaconQueryPart & { raw: string };
        }

        // Operator can be omitted, in which case value is shifted into operator
        // spot, so we set value = operator and operator as the default.
        const operator = !__value ? DEFAULT_BACON_OPERATOR : _operator;
        const _value = !__value ? _operator : __value;

        if (!isBaconOperator(operator))
          throw new BaconError(
            `Unrecognised bacon operator '${operator}' in '${partString}'.`
          );

        // Some operators allow multiple values, if this is the case we need to
        // split appropriately
        if (BACON_MULTIPLE_VALUE_OPERATORS.has(operator))
          return {
            raw: partString,
            command,
            column,
            operator,
            value: partString
              .replace(`${command}:${column}:${operator}:`, '')
              .split(BACON_MULTIPLE_VALUE_SEPERATOR),
          };

        return {
          raw: partString,
          command,
          column,
          operator,
          value: _value,
        };
      });
  }

  /**
   * Serialises query parts back to bacon query
   */
  serialiseQueryParts = (parts: BaconQueryPart[]) => {
    let string = '';

    parts.forEach((part) => {
      string += `/${part.command}:${part.column}`;
      if (isBaconFilterQueryPart(part)) {
        string += `:${part.operator}:${
          Array.isArray(part.value)
            ? part.value.join(BACON_MULTIPLE_VALUE_SEPERATOR)
            : part.value
        }`;
      }
    });

    return string.replace('/', '');
  };

  /**
   * Find the query part diffs.
   *
   * queryB defaults to the current BaconEngine query.
   */
  getQueryPartDiff(
    queryA: string,
    queryB?: string,
    command?: BaconCommandKeys
  ) {
    let queryAParts = this.getQueryParts(queryA.replaceAll(':eq:', ':'));
    let queryBParts = this.getQueryParts(queryB.replaceAll(':eq:', ':'));

    if (command) {
      queryAParts = queryAParts.filter((part) => part.command === command);
      queryBParts = queryBParts.filter((part) => part.command === command);
    }

    const changed: BaconQueryPart[] = [];

    queryAParts.forEach((partA) => {
      let matchFound = false;
      queryBParts.forEach((partB) => {
        if (partA.command === partB.command && partA.column === partB.column) {
          if (
            partA.command === BaconCommandKeys.FILTER &&
            partB.command === BaconCommandKeys.FILTER
          ) {
            if (partA.operator === partB.operator) {
              if (Array.isArray(partA.value) && Array.isArray(partB.value)) {
                if (
                  partA.value.every((valueA) =>
                    (partB.value as string[]).some(
                      (valueB) => valueA === valueB
                    )
                  ) &&
                  partB.value.every((valueB) =>
                    (partA.value as string[]).some(
                      (valueA) => valueA === valueB
                    )
                  )
                ) {
                  matchFound = true;
                }
              } else if (partA.value === partB.value) {
                matchFound = true;
              }
            }
          } else {
            matchFound = true;
          }
        }
      });
      if (!matchFound) changed.push(partA);
    });

    queryBParts.forEach((partB) => {
      let matchFound = false;
      queryAParts.forEach((partA) => {
        if (partA.command === partB.command && partA.column === partB.column) {
          if (
            partA.command === BaconCommandKeys.FILTER &&
            partB.command === BaconCommandKeys.FILTER
          ) {
            if (partA.operator === partB.operator) {
              if (Array.isArray(partA.value) && Array.isArray(partB.value)) {
                if (
                  partA.value.every((valueA) =>
                    (partB.value as string[]).some(
                      (valueB) => valueA === valueB
                    )
                  ) &&
                  partB.value.every((valueB) =>
                    (partA.value as string[]).some(
                      (valueA) => valueA === valueB
                    )
                  )
                ) {
                  matchFound = true;
                }
              } else if (partA.value === partB.value) {
                matchFound = true;
              }
            }
          } else {
            matchFound = true;
          }
        }
      });
      if (!matchFound) changed.push(partB);
    });

    return changed;
  }

  /**
   * Tries to find the bacon query part that has changed between two queries
   *
   * queryB defaults to the current BaconEngine query.
   */
  getChangedPart(queryA?: string, queryB?: string, command?: BaconCommandKeys) {
    if (!queryA || !queryB) return null;
    const diff = this.getQueryPartDiff(queryA, queryB, command);

    if (diff.length === 1) return diff[0];

    const columnMap: { [key: string]: BaconQueryPart[] } = {};

    for (const part of diff) {
      // For place holder filters like date
      if (part.value?.includes('__')) return part;
      columnMap[part.column] = [...(columnMap[part.column] || []), part];
    }

    const singleColumns = Object.values(columnMap)
      .filter((parts) => parts.length === 1)
      .flat();

    if (singleColumns.length === 1) return singleColumns[0];

    console.log('Failed to get single changed columns from:', diff);
    return singleColumns[0] || diff[0];
  }

  /**
   * Fetch bacon data based on local query
   */
  async fetch() {
    // this.events.onRequestPending?.();
    this.query = this.localQuery;
    this.pagination = this.localPagination;
    return Promise.all([
      api.get<BaconNav>(
        `${this.endpoint}/nav?q=${this.query || ''}&t=${this.pagination}`,
        {
          baseURL: this.baseUrl,
        }
      ),
      api.get<BaconTable>(
        `${this.endpoint}/table?q=${this.query || ''}&t=${this.pagination}`,
        {
          baseURL: this.baseUrl,
        }
      ),
    ])
      .then(([navResponse, tableResponse]) => {
        // this.events.onRequestFullfiled?.();
        this.nav = navResponse.data;
        this.table = tableResponse.data;

        return [this.nav, this.table];
      })
      .catch((error) => {
        console.error(error);
        // this.events.onRequestError?.();
        this.nav = DEFAULT_BACON_NAV;
        this.table = DEFAULT_BACON_TABLE;

        return [this.nav, this.table];
      });
  }
}
// #endregion
